import * as React from 'react';
import { cn } from '../../lib/cn';
import { format_date_xero } from './date_input_format';

export interface DateInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  error?: boolean;
  'data-testid'?: string;
  /** Controls how the selected date is displayed.
   * - 'xero' (default): "D Mon YYYY" (e.g. "31 Mar 2026") — matches Xero muscle memory.
   * - 'browser-default': native browser date picker chrome (platform-dependent).
   */
  displayFormat?: 'browser-default' | 'xero';
  labels?: { select_date?: string };
  /**
   * BCP 47 locale tag (e.g. `'si'`, `'ta'`, `'id'`, `'en'`).
   * Controls month abbreviations in the display format when displayFormat is 'xero'.
   * Defaults to `'en'`.
   */
  locale?: string;
}

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      className,
      error,
      onChange,
      displayFormat = 'xero',
      locale = 'en',
      'data-testid': data_testid,
      labels,
      ...props
    },
    ref,
  ) => {
    const [display_value, set_display_value] = React.useState(
      displayFormat === 'xero' && props.value
        ? format_date_xero(String(props.value), locale)
        : '',
    );
    const internal_ref = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(
      ref,
      () => internal_ref.current as HTMLInputElement,
    );

    React.useEffect(() => {
      if (displayFormat === 'xero') {
        set_display_value(
          props.value ? format_date_xero(String(props.value), locale) : '',
        );
        // Keep the uncontrolled input in sync when value prop changes externally.
        if (internal_ref.current) {
          internal_ref.current.value = props.value ? String(props.value) : '';
        }
      }
    }, [props.value, displayFormat, locale]);

    // The native date picker dispatches a 'change' event (not 'input'), but
    // React's synthetic onChange maps to 'input'. Attach a native 'change'
    // listener so picking a date from the browser calendar fires onChange.
    const on_change_ref = React.useRef(onChange);
    on_change_ref.current = onChange;
    React.useEffect(() => {
      if (displayFormat !== 'xero') return;
      const input = internal_ref.current;
      if (!input) return;
      function handle_native_change(e: Event) {
        const input = e.target as HTMLInputElement;
        set_display_value(
          input.value ? format_date_xero(input.value, locale) : '',
        );
        // Fabricated event must carry `target.name` — react-hook-form's
        // `register(...)` spread routes field updates by name, and silently
        // drops any change event whose target has no name (the update never
        // reaches form state).
        on_change_ref.current?.({
          target: { name: input.name, value: input.value },
        } as React.ChangeEvent<HTMLInputElement>);
      }
      input.addEventListener('change', handle_native_change);
      return () => {
        input.removeEventListener('change', handle_native_change);
      };
    }, [displayFormat, locale]);

    function show_picker(): void {
      const input = internal_ref.current;
      if (!input) return;
      // Focus first so showPicker() has a transient user-activation on the element.
      input.focus();
      if ('showPicker' in input && typeof input.showPicker === 'function') {
        try {
          input.showPicker();
        } catch {
          // showPicker() rejected (e.g. browser security policy).
          // Do NOT call input.click() here — that re-triggers handle_input_click
          // which calls show_picker() again, causing infinite recursion.
        }
      }
      // When showPicker is absent (jsdom / older browsers), we do nothing:
      // the native date picker opens on click anyway without a programmatic call,
      // and calling input.click() here would re-trigger handle_input_click → infinite loop.
    }

    function handle_input_click(e: React.MouseEvent<HTMLInputElement>): void {
      show_picker();
      props.onClick?.(e);
    }

    if (displayFormat === 'xero') {
      return (
        <div className="nv-date-input relative min-w-[130px]">
          {/* Calendar icon on the left — matches Xero's calendar affordance */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-faint)] pointer-events-none"
            style={{ zIndex: 1 }}
            aria-hidden="true"
          >
            <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
            <line x1="16" x2="16" y1="2" y2="6" />
            <line x1="8" x2="8" y1="2" y2="6" />
            <line x1="3" x2="21" y1="10" y2="10" />
          </svg>
          {/* ChevronDown on the right — Xero #4954: dropdown arrow indicator */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-faint)] pointer-events-none"
            style={{ zIndex: 1 }}
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
          {/* Visible display button — rendered first so the input stacks on top.
              pointer-events-none so clicks fall through to the input above it. */}
          <button
            type="button"
            tabIndex={-1}
            style={{ paddingInlineStart: '2.25rem', paddingInlineEnd: '2rem' }}
            className={cn(
              // nv-input provides border, radius and shadow matching Input / MoneyInput. The focus
              // ring comes from `.nv-date-input:has(> input:focus-visible) > .nv-input` in
              // web-glass.css, because the focusable element is the sibling <input> below (#25158).
              // Inline style overrides nv-input's padding shorthand so icons don't overlap text.
              'nv-input flex items-center h-11 w-full cursor-pointer text-base text-start whitespace-nowrap overflow-hidden pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 sm:text-[13px]',
              error && 'nv-field-error',
              className,
            )}
            aria-label={props['aria-label']}
          >
            {display_value ||
              props.placeholder ||
              (labels?.select_date ?? 'Select date')}
          </button>
          {/* Transparent native date input on top — receives clicks directly.
              onClick calls show_picker() so clicking anywhere opens the calendar,
              not just the native browser calendar icon. */}
          <input
            type="date"
            ref={internal_ref}
            defaultValue={props.value}
            id={props.id}
            name={props.name}
            data-testid={data_testid}
            aria-label={props['aria-label']}
            aria-labelledby={props['aria-labelledby']}
            aria-describedby={props['aria-describedby']}
            aria-invalid={props['aria-invalid']}
            // The `xero` branch forwards an explicit allowlist of props to the
            // real <input> rather than spreading, so `aria-required` has to be
            // named here for a required date field to expose that to AT.
            aria-required={props['aria-required']}
            disabled={props.disabled}
            min={props.min}
            max={props.max}
            onClick={handle_input_click}
            onBlur={props.onBlur}
            style={{
              position: 'absolute',
              opacity: 0,
              inset: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              zIndex: 2,
            }}
          />
        </div>
      );
    }

    return (
      <input
        type="date"
        ref={internal_ref}
        onClick={handle_input_click}
        className={cn(
          'nv-input w-full cursor-pointer text-base disabled:cursor-not-allowed disabled:opacity-50 sm:text-[13px]',
          error && 'nv-field-error',
          className,
        )}
        onChange={onChange}
        {...props}
      />
    );
  },
);
DateInput.displayName = 'DateInput';
